import { useState, useEffect, useRef } from 'react';

/* ── Mobile breakpoint hook ── */
function useIsMobile(bp = 600) {
  const [mobile, setMobile] = useState(() => window.innerWidth < bp);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < bp);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, [bp]);
  return mobile;
}
import { PAIR_GROUPS, SESSIONS, STRATEGIES, EMOTIONS, MARKET_CONDITIONS, HTF_BIAS,
         TIMEFRAMES, SETUP_GRADES, ENTRY_TYPES, EXIT_TYPES, CONFLUENCE_TAGS } from '../utils/constants.js';
import { autoCalc, calcAmount, calcRiskAmount, fmt$, autoDetectSession, calcDuration } from '../utils/helpers.js';
import { tradesApi } from '../utils/api.js';
const FINNHUB_KEY = import.meta.env.VITE_FINNHUB_KEY || '';
async function fetchDayEvents(date) {
  if (!date) return [];
  try {
    const res = await fetch(
      `https://finnhub.io/api/v1/calendar/economic?from=${date}&to=${date}&token=${FINNHUB_KEY}`
    );
    if (!res.ok) return [];
    const json = await res.json();
    return (json.economicCalendar || [])
      .filter(ev => ev.impact === 'high' || ev.impact === '3')
      .map(ev => ({ event: ev.event || ev.name || 'Unknown', impact: 'high', country: ev.country || '' }));
  } catch { return []; }
}

async function compressImage(file, { maxPx = 1280, quality = 0.82 } = {}) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (Math.max(width, height) > maxPx) {
        if (width >= height) { height = Math.round(height * maxPx / width); width = maxPx; }
        else                 { width  = Math.round(width  * maxPx / height); height = maxPx; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      const r = new FileReader();
      r.onload = ev => resolve(ev.target.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(file);
    };
    img.src = url;
  });
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ title, message, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'rgba(0,0,0,.65)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onCancel}>
      <div style={{
        background: '#111318', border: '1px solid rgba(255,255,255,.1)',
        borderRadius: 12, width: '100%', maxWidth: 400,
        boxShadow: '0 24px 64px rgba(0,0,0,.6)', overflow: 'hidden',
      }} onClick={e => e.stopPropagation()}>
        {/* Gold top bar */}
        <div style={{ height: 3, background: 'linear-gradient(90deg,var(--gold),transparent 70%)' }}/>
        {/* Header */}
        <div style={{
          padding: '18px 22px 14px',
          borderBottom: '1px solid rgba(255,255,255,.06)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
            background: 'rgba(201,168,76,.1)', border: '1px solid rgba(201,168,76,.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--white)', fontFamily: "'Inter',sans-serif" }}>
            {title}
          </div>
        </div>
        {/* Body */}
        <div style={{ padding: '18px 22px 22px' }}>
          <div style={{ fontSize: 13, color: 'rgba(240,234,214,.65)', lineHeight: 1.7, marginBottom: 22, fontFamily: "'Inter',sans-serif" }}>
            {message}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onCancel} style={{
              flex: 1, padding: '10px 0', borderRadius: 6, cursor: 'pointer',
              background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)',
              color: 'rgba(240,234,214,.55)', fontSize: 12, fontWeight: 700,
              letterSpacing: '.05em', textTransform: 'uppercase', fontFamily: "'Inter',sans-serif",
            }}>Go Back</button>
            <button onClick={onConfirm} style={{
              flex: 1, padding: '10px 0', borderRadius: 6, cursor: 'pointer',
              background: 'rgba(201,168,76,.12)', border: '1px solid rgba(201,168,76,.35)',
              color: 'var(--gold)', fontSize: 12, fontWeight: 700,
              letterSpacing: '.05em', textTransform: 'uppercase', fontFamily: "'Inter',sans-serif",
            }}>Save Anyway</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Layout helpers ── */
function Row({ cols=4, mb=14, children }){
  const mobile = useIsMobile();
  const effectiveCols = mobile ? Math.min(cols, 2) : cols;
  return(
    <div style={{display:'grid',gridTemplateColumns:`repeat(${effectiveCols},1fr)`,gap:mobile?8:10,marginBottom:mb}}
         className={`modal-row modal-row-${cols}`}>
      {children}
    </div>
  );
}
function Field({ label, children, span=1 }){
  return(
    <div className="fg" style={span>1?{gridColumn:`span ${span}`}:{}}>
      <label className="inp-label">{label}</label>
      {children}
    </div>
  );
}
function SectionDivider({ label, icon }){
  return(
    <div className="kfx-section">
      {icon && <span className="s-icon">{icon}</span>}
      {label}
    </div>
  );
}
function AutoBadge(){
  return <span className="kfx-auto-badge">AUTO</span>;
}

/* ── Voice Journal Button ── */
function VoiceButton({ onResult, targetField }) {
  const [state,      setState]      = useState('idle');
  const [transcript, setTranscript] = useState('');
  const [error,      setError]      = useState('');
  const recogRef = useRef(null);
  const finalRef = useRef('');

  const EMOTIONS_LIST = ['Calm','Confident','FOMO','Revenge','Anxious','Greedy','Excited','Fearful','Frustrated','Disciplined'];

  const processTranscript = async (raw) => {
    if (!raw.trim()) { setState('idle'); return; }
    setState('processing');
    try {
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'anthropic-dangerous-direct-browser-access': 'true' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 500,
          system: `You are a trading journal assistant. A trader just recorded a voice memo about their trade.
Your job:
1. Clean up the transcript into a concise, well-written journal note (2-4 sentences). Fix filler words, stutters, repetition.
2. Detect the primary emotion from this list only: ${EMOTIONS_LIST.join(', ')}. Pick the single best match.
Return ONLY raw JSON — no markdown, no explanation.
Schema: { "note": "cleaned note text", "emotion": "detected emotion" }`,
          messages: [{ role: 'user', content: `Voice memo for ${targetField}: "${raw}"` }],
        }),
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || '';
      const parsed = JSON.parse(text.replace(/```json|```/g, '').trim());
      onResult({ note: parsed.note || raw, emotion: parsed.emotion || '' });
      setState('done');
      setTimeout(() => { setState('idle'); setTranscript(''); finalRef.current = ''; }, 2500);
    } catch {
      onResult({ note: raw, emotion: '' });
      setState('done');
      setTimeout(() => { setState('idle'); setTranscript(''); finalRef.current = ''; }, 2500);
    }
  };

  const start = (e) => {
    // Prevent event from bubbling to modal overlay (which calls onClose → navigates away)
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setError(''); setTranscript(''); finalRef.current = '';
    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) { setError('Not supported in this browser. Try Chrome.'); return; }
      const recog = new SpeechRecognition();
      recog.continuous = true; recog.interimResults = true; recog.lang = 'en-US';
      recogRef.current = recog;
      recog.onresult = ev => {
        let interim = '';
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          if (ev.results[i].isFinal) finalRef.current += ev.results[i][0].transcript + ' ';
          else interim += ev.results[i][0].transcript;
        }
        setTranscript((finalRef.current + interim).trim());
      };
      recog.onerror = ev => { setError('Mic error: ' + ev.error); setState('idle'); };
      recog.onend   = () => processTranscript(finalRef.current.trim());
      recog.start();
      setState('listening');
      setTimeout(() => { if (recogRef.current) recogRef.current.stop(); }, 60000);
    } catch (err) {
      setError('Could not start microphone: ' + (err.message || err));
      setState('idle');
    }
  };

  const stop = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (recogRef.current) recogRef.current.stop();
  };

  const clr = {
    idle:       { bg:'rgba(201,168,76,.1)',  border:'rgba(201,168,76,.3)',  color:'var(--gold)' },
    listening:  { bg:'rgba(217,79,79,.12)',  border:'rgba(217,79,79,.5)',   color:'#D94F4F'     },
    processing: { bg:'rgba(78,127,212,.1)',  border:'rgba(78,127,212,.3)',  color:'#4E7FD4'     },
    done:       { bg:'rgba(61,190,115,.1)',  border:'rgba(61,190,115,.3)',  color:'#3DBE73'     },
  }[state];

  return (
    <div style={{ marginBottom:6 }}>
      <button type="button"
        onClick={e => { e.preventDefault(); e.stopPropagation(); if (state === 'listening') stop(e); else if (state === 'idle') start(e); }}
        disabled={state === 'processing' || state === 'done'}
        style={{
          display:'flex', alignItems:'center', gap:6,
          padding:'5px 12px',
          background:clr.bg, border:`1px solid ${clr.border}`, color:clr.color,
          fontSize:9, fontWeight:700, letterSpacing:'.1em',
          cursor: state === 'processing' || state === 'done' ? 'default' : 'pointer',
          transition:'all .2s', fontFamily:"'Inter',sans-serif",
          borderRadius: 3,
        }}>
        {state === 'idle' && <>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8" y1="23" x2="16" y2="23"/>
          </svg>
          VOICE MEMO
        </>}
        {state === 'listening' && <>
          <span style={{width:8,height:8,borderRadius:'50%',background:'#D94F4F',animation:'pulseDot .8s ease infinite',display:'inline-block'}}/>
          TAP TO STOP
        </>}
        {state === 'processing' && <>
          <span style={{width:8,height:8,border:'1.5px solid rgba(78,127,212,.3)',borderTopColor:'#4E7FD4',borderRadius:'50%',display:'inline-block',animation:'spin 1s linear infinite'}}/>
          AI PROCESSING…
        </>}
        {state === 'done' && <>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
          FILLED ✓
        </>}
      </button>
      {state === 'listening' && transcript && (
        <div style={{marginTop:5,fontSize:10,color:'rgba(200,192,168,.45)',fontStyle:'italic',lineHeight:1.5,padding:'5px 8px',background:'rgba(217,79,79,.05)',border:'1px solid rgba(217,79,79,.12)',borderRadius:3}}>
          "{transcript}"
        </div>
      )}
      {error && <div style={{marginTop:4,fontSize:9,color:'#D94F4F'}}>{error}</div>}
    </div>
  );
}

/* Star rating 1–5 */
function StarRating({ value, onChange, color='var(--gold)' }){
  return(
    <div style={{display:'flex',gap:4,marginTop:4}}>
      {[1,2,3,4,5].map(n=>(
        <button key={n} type="button" onClick={()=>onChange(n)}
          style={{background:'none',border:'none',cursor:'pointer',padding:0,fontSize:18,lineHeight:1,
            color: n<=value ? color : 'rgba(255,255,255,.1)', transition:'color .15s'}}>
          ★
        </button>
      ))}
    </div>
  );
}

const CHIP_COLOR_MAP = {
  'var(--gold)':  '201,168,76',
  'var(--green)': '61,190,115',
  'var(--red)':   '217,79,79',
  'var(--blue)':  '78,127,212',
  '#4E7FD4':      '78,127,212',
  '#3DBE73':      '61,190,115',
  '#D94F4F':      '217,79,79',
};

const TAG_ICON = {
  'Order Block':        '🟦',
  'FVG':                '⚡',
  'Liquidity Sweep':    '💧',
  'Break of Structure': '🔺',
  'Session Killzone':   '🕐',
  'Trendline':          '📐',
  'EMA Confluence':     '〰️',
  'Supply/Demand':      '⚖️',
  'Fibonacci':          '🌀',
  'CHOCH':              '🔄',
};

function Chip({ label, active, onClick, color='var(--gold)', showIcon=false }){
  const rgb = CHIP_COLOR_MAP[color] || '201,168,76';
  const icon = showIcon ? TAG_ICON[label] : null;
  return(
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: icon ? 5 : 0,
        padding: showIcon ? '6px 13px' : '5px 12px',
        border: `1px solid ${active ? `rgba(${rgb},.55)` : 'rgba(255,255,255,.1)'}`,
        borderRadius: 3,
        cursor: 'pointer',
        fontFamily: "'Inter',sans-serif",
        fontSize: showIcon ? 11 : 10,
        letterSpacing: '.04em',
        fontWeight: active ? 700 : 500,
        transition: 'all .15s ease',
        background: active
          ? `linear-gradient(135deg,rgba(${rgb},.18),rgba(${rgb},.08))`
          : 'rgba(255,255,255,.03)',
        color: active ? color : 'rgba(245,240,232,.55)',
        boxShadow: active ? `0 0 10px rgba(${rgb},.12), inset 0 1px 0 rgba(${rgb},.1)` : 'none',
        transform: active ? 'translateY(-1px)' : 'none',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={e => {
        if (!active) {
          e.currentTarget.style.borderColor = `rgba(${rgb},.3)`;
          e.currentTarget.style.color = `rgba(245,240,232,.8)`;
          e.currentTarget.style.background = `rgba(${rgb},.06)`;
        }
      }}
      onMouseLeave={e => {
        if (!active) {
          e.currentTarget.style.borderColor = 'rgba(255,255,255,.1)';
          e.currentTarget.style.color = 'rgba(245,240,232,.55)';
          e.currentTarget.style.background = 'rgba(255,255,255,.03)';
        }
      }}
    >
      {icon && <span style={{ fontSize: 11, lineHeight: 1 }}>{icon}</span>}
      {label}
      {active && (
        <span style={{
          marginLeft: 5,
          width: 5, height: 5,
          borderRadius: '50%',
          background: color,
          display: 'inline-block',
          boxShadow: `0 0 5px ${color}`,
          flexShrink: 0,
        }}/>
      )}
    </button>
  );
}

function isWeekend(dateStr) {
  if (!dateStr) return false;
  const [y, m, d] = dateStr.split('-').map(Number);
  const day = new Date(y, m - 1, d).getDay();
  return day === 0 || day === 6;
}

const EMPTY = {
  pair:'', dir:'', date:new Date().toISOString().slice(0,10), time:'', exitDate:'', exitTime:'',
  session:'',
  entry:'', sl:'', tp:'', lotSize:'', accountSize:'', riskPct:'',
  actualEntry:'', actualExit:'',
  marketCondition:'', htfBias:'', htfTf:'H4', entryTf:'M15', timeframe:'',
  setupGrade:'', followedPlan:'', entryType:'', exitType:'',
  plannedRR:'',
  confluences:[],
  ruleChecks:[],
  movedSl:false, partialClose:false, heldToTp:false,
  emotion:'', confidence:0, discipline:0, stressLevel:0,
  newsPresent:false,
  mfe:'', mae:'',
  strat:'', notes:'', review:'',
  imgBefore:'', imgAfter:'',
};

export default function TradeModal({ initial, draft, onSave, onClose, onDismiss, activeAccount: propActiveAccount, rules: propRules }){
  const isMobile = useIsMobile();
  const DEFAULT_RULES = ['Waited for Confirmation', 'Risked < 1% of Account', 'No Revenge / Emotional Entry'];

  // ── Internal account state — self-sufficient fallback ──────────────────────
  // If the parent doesn't pass activeAccount (old wiring), we listen to the
  // kfx:accounts-updated event directly so the modal always has the right account.
  const [internalAccounts,  setInternalAccounts]  = useState([]);
  const [internalActiveId,  setInternalActiveId]  = useState(null);

  useEffect(() => {
    // Seed from user-scoped sessionStorage — key is kfx:accounts:{userId}
    // Current user id is stored separately under kfx:current_user
    try {
      const uid    = sessionStorage.getItem('kfx:current_user') || '';
      const cached = uid ? sessionStorage.getItem(`kfx:accounts:${uid}`) : null;
      if (cached) {
        const { tradingAccounts, activeAccountId } = JSON.parse(cached);
        if (Array.isArray(tradingAccounts)) setInternalAccounts(tradingAccounts);
        if (activeAccountId) setInternalActiveId(activeAccountId);
      }
    } catch (_) {}

    const handler = ({ detail }) => {
      if (Array.isArray(detail.tradingAccounts)) setInternalAccounts(detail.tradingAccounts);
      if (detail.activeAccountId !== undefined)  setInternalActiveId(detail.activeAccountId);
    };
    window.addEventListener('kfx:accounts-updated', handler);
    return () => window.removeEventListener('kfx:accounts-updated', handler);
  }, []);

  // Prop takes priority; fall back to internal event-sourced account
  const resolvedAccount     = propActiveAccount
    ?? internalAccounts.find(a => a.id === internalActiveId)
    ?? null;

  // Derive account details
  const propAccountSize   = String(resolvedAccount?.balance || '');
  const activeAccountId   = resolvedAccount?.id   || null;
  const activeAccountName = resolvedAccount?.name || null;

  // Ref that always holds the latest activeAccountId — doSave reads this
  // instead of the closure variable to avoid stale-closure bugs where
  // accountId saves as NULL because the value was captured before resolvedAccount
  // was populated (e.g. on the first render before the useEffect seed fires).
  const activeAccountIdRef = useRef(activeAccountId);
  useEffect(() => { activeAccountIdRef.current = activeAccountId; }, [activeAccountId]);

  const deriveQuality = (ruleChecks, rulesArr) => {
    const rules   = (rulesArr && rulesArr.length > 0) ? rulesArr : DEFAULT_RULES;
    const total   = rules.length;
    if (total === 0) return { followedPlan: '', setupGrade: '' };
    const checks  = Array.from({ length: total }, (_, i) => (ruleChecks || [])[i] || false);
    const passed  = checks.filter(Boolean).length;
    const missed  = total - passed;
    const followedPlan = passed === total ? true : false;
    let setupGrade;
    if      (missed === 0) setupGrade = 'A+';
    else if (missed === 1) setupGrade = 'A';
    else if (missed <= Math.ceil(total * 0.4)) setupGrade = 'B';
    else    setupGrade = 'C';
    return { followedPlan, setupGrade };
  };

  const [form, setForm] = useState(() => ({
    ...EMPTY,
    accountSize: propAccountSize,
    // Edit mode: use initial. New trade: restore draft if one exists, else empty.
    ...(initial ? initial : (draft || {})),
  }));
  const [prevBefore, setPrevBefore] = useState(initial?.imgBefore || initial?.img || '');
  const [prevAfter,  setPrevAfter]  = useState(initial?.imgAfter || '');
  const [tab,          setTab]          = useState(0);
  const [saving,       setSaving]       = useState(false);
  const [saveError,    setSaveError]    = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [confirmDialog,    setConfirmDialog]    = useState(null);
  const [marketEvents, setMarketEvents] = useState([]);
  const [eventsLoading,setEventsLoading]= useState(false);
  // "Other" custom-input support for Strategy and Confluences —
  // lets a trader type their own value when nothing in the preset list fits.
  const [showCustomStrategy, setShowCustomStrategy] = useState(
    () => !!(initial?.strat) && !STRATEGIES.includes(initial.strat)
  );
  const [showCustomConfluence, setShowCustomConfluence] = useState(false);
  const [customConfluenceInput, setCustomConfluenceInput] = useState('');

  const set = (k,v) => { setValidationErrors([]); setForm(f => ({...f,[k]:v})); }

  // Sync account balance into the form whenever the resolved account changes.
  // Only overwrites if the user hasn't manually typed a different value.
  useEffect(() => {
    if (!propAccountSize) return;
    setForm(f => {
      // Don't overwrite if the user has already manually edited the field
      // (i.e. it differs from any previously auto-filled value)
      const alreadyManual = f.accountSize && f.accountSize !== propAccountSize;
      if (alreadyManual) return f;
      return { ...f, accountSize: propAccountSize };
    });
  }, [propAccountSize]);

  useEffect(() => {
    if (!form.date) return;
    setEventsLoading(true);
    fetchDayEvents(form.date).then(evs => {
      setMarketEvents(evs);
      setEventsLoading(false);
      if (evs.length > 0) set('newsPresent', true);
    });
  }, [form.date]);

  useEffect(()=>{
    if(form.time && !form.session){
      const s = autoDetectSession(form.time);
      if(s) set('session', s);
    }
  },[form.time]);

  // Risk $ amount — uses the SAME per-instrument pip/point specs as
  // calcAmount (Estimated P&L), so Risk % stays consistent across
  // forex, gold/silver, JPY pairs, indices, and crypto.
  const riskAmt = calcRiskAmount(form.entry, form.sl, form.lotSize, form.pair);
  const riskPct = riskAmt && form.accountSize ? ((riskAmt/parseFloat(form.accountSize))*100).toFixed(2) : null;

  const calc   = autoCalc(form.entry, form.sl, form.tp, form.dir, form.pair);
  const amount = calcAmount(form.entry, form.sl, form.tp, form.dir, form.lotSize, form.pair);
  // Resolve the real exit date:
  // 1. If the user has explicitly set an exit date, use it.
  // 2. Otherwise fall back to the overnight heuristic (exitTime < time → +1 day).
  // 3. Otherwise the exit is on the same day as entry.
  const exitDate = (() => {
    if (form.exitDate) return form.exitDate;
    if (form.date && form.time && form.exitTime && form.exitTime < form.time) {
      const d = new Date(form.date);
      d.setDate(d.getDate() + 1);
      return d.toISOString().slice(0, 10);
    }
    return form.date;
  })();
  const duration = calcDuration(form.date, form.time, form.exitTime, exitDate);

  // Actual R:R — only computed when we have a real exit price (not planned TP)
  const actualRR = (() => {
    const e  = parseFloat(form.actualEntry || form.entry);
    const ex = parseFloat(form.actualExit);   // must be explicitly filled — never fall back to TP
    const sl = parseFloat(form.sl);
    if (!e || !ex || !sl) return null;
    const risk   = Math.abs(e - sl);
    const reward = Math.abs(ex - e);
    return risk > 0 ? (reward / risk).toFixed(2) : null;
  })();

  // Auto-derive exit outcome from actual prices vs planned levels
  const derivedOutcome = (() => {
    const ae  = parseFloat(form.actualEntry || form.entry);
    const ax  = parseFloat(form.actualExit);
    const sl  = parseFloat(form.sl);
    const tp  = parseFloat(form.tp);
    const dir = form.dir;
    if (!ae || !ax || !sl || !tp || !dir) return null;
    const tol = Math.abs(tp - ae) * 0.015; // 1.5% tolerance
    if (Math.abs(ax - tp) <= tol)   return { exitType: 'TP Hit',     pnl: 'win' };
    if (Math.abs(ax - sl) <= tol)   return { exitType: 'SL Hit',     pnl: 'loss' };
    if (Math.abs(ax - ae) <= tol)   return { exitType: 'Break-Even', pnl: 'be' };
    if (dir === 'BUY'  && ax > ae)  return { exitType: 'Manual',     pnl: 'win' };
    if (dir === 'BUY'  && ax < ae)  return { exitType: 'Manual',     pnl: 'loss' };
    if (dir === 'SELL' && ax < ae)  return { exitType: 'Manual',     pnl: 'win' };
    if (dir === 'SELL' && ax > ae)  return { exitType: 'Manual',     pnl: 'loss' };
    return null;
  })();

  // Compute actual P&L from actual exit (if available), otherwise fall back to planned TP
  const actualAmount = (() => {
    const ae  = parseFloat(form.actualEntry || form.entry);
    const ax  = parseFloat(form.actualExit);
    const lot = parseFloat(form.lotSize);
    if (!ae || !ax || !lot) return null;
    // Pass actualExit as the "tp" argument since calcAmount computes priceDiff = tp - entry
    return calcAmount(form.actualEntry || form.entry, form.sl, String(ax), form.dir, form.lotSize, form.pair);
  })();

  const toggleConfluence = tag => {
    const cur = form.confluences || [];
    set('confluences', cur.includes(tag) ? cur.filter(t=>t!==tag) : [...cur,tag]);
  };

  const [imgUploading, setImgUploading] = useState({ before: false, after: false });

  const handleImg = async (which, e) => {
    const f = e.target.files[0]; if (!f) return;
    const key = which === 'before' ? 'before' : 'after';
    setImgUploading(s => ({ ...s, [key]: true }));
    try {
      const dataUrl = await compressImage(f);
      if (!dataUrl) return;
      if (which === 'before') { setPrevBefore(dataUrl); set('imgBefore', dataUrl); }
      else                    { setPrevAfter(dataUrl);  set('imgAfter',  dataUrl); }
    } finally {
      setImgUploading(s => ({ ...s, [key]: false }));
    }
  };

  const doSave = async () => {
    setSaving(true);
    setSaveError('');
    const pips   = parseFloat(calc.pips) || 0;
    // Use actual P&L from actualExit when available; fall back to planned TP P&L
    const result = actualAmount !== null ? actualAmount : (amount !== null ? amount : 0);
    // Auto-derive exitType from actual prices if user has not set it
    const resolvedExitType = form.exitType || (derivedOutcome ? derivedOutcome.exitType : "");
    const { followedPlan: derivedFollowed, setupGrade: derivedGrade } = deriveQuality(form.ruleChecks, propRules);

    // Dev guard — if accountId is still null here something is wrong upstream
    if (!activeAccountIdRef.current) {
      console.warn('[TradeModal] saving with null accountId — resolvedAccount:', resolvedAccount,
        '| propActiveAccount:', propActiveAccount, '| internalActiveId:', internalActiveId);
    }

    // If TP was hit, MFE = TP (price reached the target — that IS the best price reached)
    const resolvedExitTypeForMfe = form.exitType || (derivedOutcome ? derivedOutcome.exitType : '');
    const mfeValue = resolvedExitTypeForMfe === 'TP Hit'
      ? (parseFloat(form.tp) || form.mfe || null)
      : (form.mfe || null);

    const payload = {
      marketEvents,
      ...form,
      mfe:         mfeValue,
      accountId:   activeAccountIdRef.current,   // always fresh — never a stale closure
      pips:        result >= 0 ? Math.abs(pips) : -Math.abs(pips),
      rr:          calc.rr || '0.00',
      actualRR:    actualRR || calc.rr || '0.00',
      result,
      lotSize:     parseFloat(form.lotSize) || 0,
      riskAmount:  riskAmt  || form.riskAmount || null,
      riskPct:     riskPct || form.riskPct,
      duration:    duration || '',
      img:         form.imgBefore,
      followedPlan: (form.ruleChecks||[]).some(Boolean) ? derivedFollowed : form.followedPlan,
      setupGrade:   (form.ruleChecks||[]).some(Boolean) ? derivedGrade   : form.setupGrade,
      exitType:    resolvedExitType,
      ruleChecks:  Array.isArray(form.ruleChecks) ? form.ruleChecks : [],
      htfTf:       form.htfTf    || 'H4',
      entryTf:     form.entryTf  || 'M15',
      plannedRR:   calc.rr       || null,
    };
    try {
      let savedTrade;
      if (initial?.id) {
        savedTrade = await tradesApi.update(initial.id, payload);
      } else {
        savedTrade = await tradesApi.create(payload);
      }
      onSave(savedTrade);
    } catch (err) {
      setSaveError(err.message || 'Failed to save trade. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (saving) return;
    setSaveError('');
    const errors = [];
    if (!form.pair)  errors.push('Pair is required.');
    if (!form.dir)   errors.push('Direction (BUY/SELL) is required.');
    if (!form.date)  errors.push('Date is required.');
    if (form.date) {
      const d = new Date(form.date);
      const today = new Date(); today.setHours(23,59,59,999);
      if (isNaN(d.getTime()))  errors.push('Date is not valid.');
      else if (d > today)      errors.push('Date cannot be in the future.');
      else if (isWeekend(form.date)) errors.push('Date falls on a weekend — forex markets are closed on Saturday and Sunday.');
    }
    const entry = parseFloat(form.entry);
    const sl    = parseFloat(form.sl);
    const tp    = parseFloat(form.tp);
    const lot   = parseFloat(form.lotSize);
    if (form.entry   !== '' && (isNaN(entry) || entry <= 0)) errors.push('Entry price must be a positive number.');
    if (form.sl      !== '' && (isNaN(sl)    || sl    <= 0)) errors.push('Stop Loss must be a positive number.');
    if (form.tp      !== '' && (isNaN(tp)    || tp    <= 0)) errors.push('Take Profit must be a positive number.');
    if (form.lotSize !== '' && (isNaN(lot)   || lot   <= 0)) errors.push('Lot size must be greater than 0.');
    if (!isNaN(entry) && entry > 0 && !isNaN(sl) && sl > 0 && !isNaN(tp) && tp > 0) {
      if (form.dir === 'BUY') {
        if (sl >= entry) errors.push('BUY trade: Stop Loss must be below Entry price.');
        if (tp <= entry) errors.push('BUY trade: Take Profit must be above Entry price.');
      } else if (form.dir === 'SELL') {
        if (sl <= entry) errors.push('SELL trade: Stop Loss must be above Entry price.');
        if (tp >= entry) errors.push('SELL trade: Take Profit must be below Entry price.');
      }
    }
    // Note: exitTime < time (string compare) means a next-day exit — this is valid for
    // overnight trades (e.g. entry 22:00, exit 02:30 next day). We do NOT block saving;
    // the amber inline warning on the Exit Time field already alerts the user.
    // unmapTrade in api.js handles the +1 day offset when building the closedAt ISO timestamp.
    if (form.exitDate && form.date && form.exitDate < form.date) {
      errors.push('Exit date cannot be before the entry date.');
    }
    if (form.exitDate && form.date && form.exitDate === form.date && form.time && form.exitTime && form.exitTime < form.time) {
      errors.push('Exit time is before entry time on the same day — either fix the times or set a later Exit Date.');
    }
    if (!(form.ruleChecks || []).some(Boolean)) {
      errors.push('Rule Adherence: check at least one rule so Setup Grade and Followed Plan can be auto-graded (Execution tab).');
    }
    if (errors.length) { setValidationErrors(errors); return; }
    setValidationErrors([]);
    if (amount === null) {
      setConfirmDialog({ onConfirm: () => { setConfirmDialog(null); doSave(); } });
      return;
    }
    doSave();
  };

  const TABS = [
    { label:'Trade',      icon:'📋', hint:'Pair, direction, prices'   },
    { label:'Result',     icon:'✅', hint:'What actually happened'     },
    { label:'Setup',      icon:'🌍', hint:'Market context & confluences'},
    { label:'Mindset',    icon:'🧠', hint:'Emotion & discipline'       },
  ];

  /* ── Live sub-line for header ── */
  const headerSub = (() => {
    if (!form.pair && !form.dir) return (
      <span style={{color:'rgba(245,240,232,.35)'}}>
        Step 1 of 4 · Start with the Trade tab — everything else is optional
      </span>
    );
    const parts = [];
    if (form.pair) parts.push(<span key="p" style={{color:'var(--gold)'}}>{form.pair}</span>);
    if (form.dir)  parts.push(<span key="d" style={{color:form.dir==='BUY'?'var(--green)':'var(--red)'}}>{form.dir}</span>);
    if (form.date) parts.push(<span key="dt">{form.date}</span>);
    if (actualAmount !== null) parts.push(
      <span key="a" style={{color:actualAmount>=0?'var(--green)':'var(--red)',fontWeight:700}}>
        {actualAmount>=0?'+':'-'}${Math.abs(actualAmount).toFixed(2)} actual
      </span>
    );
    else if (amount !== null) parts.push(
      <span key="a" style={{color:amount>=0?'var(--green)':'var(--red)',fontWeight:700}}>
        {amount>=0?'+':'-'}${Math.abs(amount).toFixed(2)} est.
      </span>
    );
    return parts.reduce((acc, el, i) => i===0 ? [el] : [...acc, <span key={`sep${i}`} style={{opacity:.3}}> · </span>, el], []);
  })();

  return(
    <>
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      background: 'rgba(0,0,0,.65)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
      padding: isMobile ? 0 : 20,
    }} onClick={() => (onDismiss || onClose)(form)}>
      <div style={{
        background: '#111318',
        border: '1px solid rgba(255,255,255,.1)',
        borderRadius: isMobile ? '16px 16px 0 0' : 12,
        width: '100%', maxWidth: isMobile ? '100%' : 780,
        maxHeight: isMobile ? '96vh' : '94vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,.7)',
        overflow: 'hidden',
      }} onClick={e=>e.stopPropagation()}>

        {/* Gold accent bar */}
        <div style={{ height: 3, background: 'linear-gradient(90deg,var(--gold),transparent 60%)', flexShrink: 0 }}/>

        {/* ── Header ── */}
        <div style={{
          padding: isMobile ? '14px 16px' : '16px 22px',
          borderBottom: '1px solid rgba(255,255,255,.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0, background: 'rgba(255,255,255,.02)',
        }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 3, fontFamily: "'Inter',sans-serif" }}>
              KFX Journal · {initial ? 'Edit Trade' : 'Log New Trade'}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--white)', letterSpacing: '-.02em', fontFamily: "'Inter',sans-serif" }}>
              {initial ? 'Edit Trade' : 'Log New Trade'}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(245,240,232,.4)', marginTop: 3, fontFamily: "'Inter',sans-serif" }}>
              {headerSub}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'rgba(240,234,214,.3)',
            fontSize: 24, lineHeight: 1, cursor: 'pointer', padding: '0 4px',
            transition: 'color .14s', flexShrink: 0,
          }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(240,234,214,.8)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(240,234,214,.3)'}>×</button>
        </div>

        {/* ── Tab bar ── */}
        <div style={{
          display: 'flex', flexShrink: 0,
          borderBottom: '1px solid rgba(255,255,255,.06)',
          background: 'rgba(255,255,255,.01)',
          overflowX: 'auto',
        }}>
          {TABS.map((t,i)=>(
            <button key={i} onClick={()=>setTab(i)} style={{
              flex: 1, minWidth: isMobile ? 60 : 80,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 3, padding: isMobile ? '10px 8px' : '12px 10px',
              background: 'none', border: 'none', borderBottom: tab===i ? '2px solid var(--gold)' : '2px solid transparent',
              color: tab===i ? 'var(--gold)' : 'rgba(245,240,232,.4)',
              cursor: 'pointer', transition: 'all .15s', fontFamily: "'Inter',sans-serif",
              fontSize: isMobile ? 9 : 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
            }}>
              <span style={{ fontSize: isMobile ? 16 : 14, lineHeight: 1 }}>{t.icon}</span>
              <span>{t.label}</span>
              {tab===i && !isMobile && <span style={{fontSize:8,opacity:.45,fontWeight:400,letterSpacing:'.04em'}}>{t.hint}</span>}
            </button>
          ))}
        </div>

        {/* ── Scrollable body ── */}
        <div style={{ padding: isMobile ? '14px 12px' : 20, overflowY: 'auto', flex: 1 }}>

          {/* Draft restored banner — shown only when a draft was recovered */}
          {!initial && draft && (
            <div style={{
              marginBottom: 14, padding: '9px 14px',
              background: 'rgba(201,168,76,.07)', border: '1px solid rgba(201,168,76,.2)',
              borderRadius: 4, display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 14 }}>💾</span>
              <div style={{ flex: 1, fontSize: 10, color: 'rgba(240,234,214,.6)', lineHeight: 1.5 }}>
                <span style={{ color: 'var(--gold)', fontWeight: 700 }}>Draft restored</span>
                {' '}— your unsaved data was kept. Hit <strong style={{ color: 'rgba(240,234,214,.8)' }}>Cancel</strong> to discard it.
              </div>
            </div>
          )}

          {/* ══ TAB 0: CORE ══ */}
          {tab===0 && <>
            <SectionDivider label="What Did You Trade?" icon="📌"/>
            <Row cols={5} mb={16}>
              <Field label="Pair *">
                <select className="inp" value={form.pair} onChange={e=>set('pair',e.target.value)}>
                  <option value="">-- Select --</option>
                  {PAIR_GROUPS.map(g=>(
                    <optgroup key={g.label} label={g.label}>
                      {g.pairs.map(p=><option key={p} value={p}>{p}</option>)}
                    </optgroup>
                  ))}
                </select>
              </Field>
              <Field label="Direction *">
                <select className="inp" value={form.dir} onChange={e=>set('dir',e.target.value)}
                  style={form.dir?{color:form.dir==='BUY'?'var(--green)':'var(--red)',fontWeight:700}:{}}>
                  <option value="">--</option>
                  <option value="BUY">▲ BUY</option>
                  <option value="SELL">▼ SELL</option>
                </select>
              </Field>
              <Field label="Date *">
                <input className="inp" type="date" value={form.date}
                  max={new Date().toISOString().slice(0,10)}
                  onChange={e=>set('date',e.target.value)}
                  style={isWeekend(form.date)?{borderColor:'rgba(217,79,79,.6)',background:'rgba(217,79,79,.06)'}:{}}/>
                {isWeekend(form.date) && (
                  <div style={{fontSize:9,color:'#D94F4F',marginTop:3,fontWeight:600}}>⛔ Weekend — markets are closed</div>
                )}
              </Field>
              <Field label="Entry Time">
                <input className="inp" type="time" value={form.time||''} onChange={e=>set('time',e.target.value)}/>
              </Field>
              <Field label={<>Session<AutoBadge/></>}>
                <select className="inp" value={form.session} onChange={e=>set('session',e.target.value)}>
                  <option value="">--</option>
                  {SESSIONS.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </Row>

            <SectionDivider label="Your Trade Plan  ·  Before You Enter" icon="📐"/>
            <div style={{marginBottom:10,fontSize:10,color:'rgba(245,240,232,.35)',lineHeight:1.6,padding:'6px 12px',background:'rgba(201,168,76,.04)',border:'1px solid rgba(201,168,76,.08)',borderRadius:3}}>
              ✏️ <strong style={{color:'rgba(201,168,76,.6)'}}>Analysis / Forecast</strong> — Enter your <em>planned</em> levels before placing the trade. P&amp;L will be estimated automatically.
            </div>
            <Row cols={4} mb={16}>
              {[['Entry Price','entry','e.g. 2145.50'],['Stop Loss','sl','e.g. 2138.00'],['Take Profit','tp','e.g. 2162.00'],['Lot Size','lotSize','e.g. 0.10']].map(([l,k,ph])=>(
                <Field key={k} label={l}>
                  <input className="inp" type="number" step="any" min={k==='lotSize'?'0.001':'0.000001'} placeholder={ph} value={form[k]} onChange={e=>set(k,e.target.value)}
                    style={k==='sl'&&form.sl?{color:'var(--red)'}:k==='tp'&&form.tp?{color:'var(--green)'}:{}}/>
                </Field>
              ))}
            </Row>
            {form.dir && form.entry && form.sl && (() => {
              const e = parseFloat(form.entry), s = parseFloat(form.sl), t = parseFloat(form.tp);
              const warns = [];
              if (form.dir === 'BUY'  && !isNaN(e) && !isNaN(s) && s >= e) warns.push('⚠ BUY: Stop Loss should be below Entry');
              if (form.dir === 'BUY'  && !isNaN(e) && !isNaN(t) && t <= e) warns.push('⚠ BUY: Take Profit should be above Entry');
              if (form.dir === 'SELL' && !isNaN(e) && !isNaN(s) && s <= e) warns.push('⚠ SELL: Stop Loss should be above Entry');
              if (form.dir === 'SELL' && !isNaN(e) && !isNaN(t) && t >= e) warns.push('⚠ SELL: Take Profit should be below Entry');
              if (!warns.length) return null;
              return (
                <div style={{marginBottom:14,padding:'8px 12px',background:'rgba(217,79,79,.08)',border:'1px solid rgba(217,79,79,.25)',borderRadius:3}}>
                  {warns.map((w,i) => <div key={i} style={{fontSize:10,color:'#D94F4F',fontWeight:600}}>{w}</div>)}
                </div>
              );
            })()}

            <SectionDivider label="Calculated for You" icon="⚡"/>
            <Row cols={4} mb={4}>
              <Field label="Distance (Pips)">
                <input className="inp" readOnly value={calc.pips||'—'}/>
              </Field>
              <Field label="Planned Risk:Reward">
                <input className="inp" readOnly value={calc.rr?`${calc.rr}R`:'—'}/>
              </Field>
              <Field label="Estimated P&L">
                <input className="inp" readOnly value={amount!==null?fmt$(amount):'—'}
                  style={{color:amount>0?'var(--green)':amount<0?'var(--red)':'var(--gold)',
                    fontFamily:"'Bebas Neue',sans-serif",fontSize:16}}/>
              </Field>
              <Field label="Risk of Account">
                <input className="inp" readOnly value={riskPct?`${riskPct}%`:'—'}
                  style={{color:riskPct>2?'var(--red)':riskPct?'var(--green)':'rgba(245,240,232,.4)'}}/>
              </Field>
            </Row>
            <div style={{marginBottom:16,fontSize:9,color:'rgba(245,240,232,.22)',letterSpacing:'.04em',paddingLeft:2}}>
              ⚡ These update live as you type — based on your planned Entry, SL and TP above
            </div>

            <Row cols={4} mb={16}>
              <Field label="Account Size ($)">
                <input className="inp" type="number" step="any" placeholder="e.g. 10000"
                  value={form.accountSize||''}
                  readOnly
                  style={{
                    cursor:'not-allowed',
                    opacity: propAccountSize ? 1 : 0.45,
                    borderColor:'rgba(46,204,138,.25)',
                    background:'rgba(46,204,138,.04)',
                    color:'rgba(240,234,214,.7)',
                  }}
                />
                {activeAccountName
                  ? <div style={{fontSize:8,color:'rgba(46,204,138,.55)',marginTop:3,display:'flex',alignItems:'center',gap:4}}>
                      <span style={{width:5,height:5,borderRadius:'50%',background:'var(--green)',display:'inline-block',flexShrink:0}}/>
                      {activeAccountName}
                    </div>
                  : <div style={{fontSize:8,color:'rgba(245,240,232,.2)',marginTop:3}}>No account selected</div>
                }
              </Field>
              <Field label="Exit Date / Time" span={1}>
                <div style={{display:'flex',gap:5}}>
                  <input className="inp" type="date" value={form.exitDate||''}
                    min={form.date||undefined}
                    max={new Date().toISOString().slice(0,10)}
                    onChange={e=>set('exitDate',e.target.value)}
                    style={{flex:'1 1 0',minWidth:0}}
                    placeholder="exit date"
                  />
                  <input className="inp" type="time" value={form.exitTime||''}
                    onChange={e=>set('exitTime',e.target.value)}
                    style={{flex:'1 1 0',minWidth:0}}
                  />
                </div>
                {/* Swing trade badge — exit date differs from entry date */}
                {form.exitDate && form.date && form.exitDate !== form.date && form.exitDate > form.date && (
                  <div style={{fontSize:9,color:'#4E7FD4',marginTop:3,lineHeight:1.5}}>
                    📅 Swing trade — <strong>{Math.round((new Date(form.exitDate)-new Date(form.date))/(1000*60*60*24))} day{Math.round((new Date(form.exitDate)-new Date(form.date))/(1000*60*60*24))!==1?'s':''}</strong> held. closedAt and duration adjusted automatically.
                  </div>
                )}
                {/* Overnight warning — same date but exit time wraps midnight */}
                {(!form.exitDate || form.exitDate === form.date) && form.time && form.exitTime && form.exitTime < form.time && (
                  <div style={{fontSize:9,color:'#E2A542',marginTop:3,lineHeight:1.5}}>
                    🌙 Exit is earlier than entry — treated as <strong>next-day exit</strong> (+1 day). Set an Exit Date above to be explicit.
                  </div>
                )}
                {/* Error: exit date before entry date */}
                {form.exitDate && form.date && form.exitDate < form.date && (
                  <div style={{fontSize:9,color:'#D94F4F',marginTop:3,fontWeight:600}}>⛔ Exit date is before entry date.</div>
                )}
              </Field>
              <Field label="Trade Duration">
                <input className="inp" readOnly value={duration||'—'}
                  style={{color: duration ? 'rgba(245,240,232,.7)' : 'rgba(245,240,232,.3)'}}/>
                <div style={{fontSize:8,color:'rgba(245,240,232,.25)',marginTop:2}}>Calculated from Entry → Exit time</div>
              </Field>
              <Field label="Strategy">
                <select className="inp" value={showCustomStrategy ? 'Other' : form.strat}
                  onChange={e=>{
                    const v = e.target.value;
                    if (v === 'Other') { setShowCustomStrategy(true); set('strat', ''); }
                    else { setShowCustomStrategy(false); set('strat', v); }
                  }}>
                  <option value="">--</option>
                  {STRATEGIES.map(s=><option key={s} value={s}>{s}</option>)}
                  {!STRATEGIES.includes('Other') && <option value="Other">Other</option>}
                </select>
                {showCustomStrategy && (
                  <input className="inp" style={{marginTop:6}} placeholder="Type your strategy name…"
                    value={form.strat} onChange={e=>set('strat', e.target.value)} autoFocus/>
                )}
              </Field>
            </Row>

            {/* ── Market Events Banner ── */}
            {(eventsLoading || marketEvents.length > 0) && (
              <div style={{marginBottom:14,padding:'10px 14px',background:'rgba(78,127,212,.07)',border:'1px solid rgba(78,127,212,.18)',borderRadius:4,display:'flex',alignItems:'flex-start',gap:10}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4E7FD4" strokeWidth="2" style={{flexShrink:0,marginTop:1}}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:9,fontWeight:700,color:'#4E7FD4',letterSpacing:'.1em',marginBottom:4}}>
                    MARKET EVENTS — {form.date}
                    {eventsLoading && <span style={{marginLeft:6,opacity:.5}}>fetching…</span>}
                  </div>
                  {eventsLoading ? (
                    <div style={{fontSize:10,color:'rgba(200,192,168,.3)'}}>Checking economic calendar…</div>
                  ) : (
                    <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
                      {marketEvents.map((ev,i) => (
                        <span key={i} style={{fontSize:9,padding:'2px 8px',background:'rgba(217,79,79,.15)',border:'1px solid rgba(217,79,79,.3)',color:'#D94F4F',fontWeight:600,letterSpacing:'.04em',borderRadius:2}}>
                          ⚡ {ev.event}{ev.country ? ` (${ev.country})` : ''}
                        </span>
                      ))}
                    </div>
                  )}
                  {!eventsLoading && marketEvents.length > 0 && (
                    <div style={{fontSize:9,color:'rgba(200,192,168,.3)',marginTop:5}}>
                      newsPresent auto-set to Yes · AI Coach will correlate these events across all your trades
                    </div>
                  )}
                  {!eventsLoading && marketEvents.length === 0 && (
                    <div style={{fontSize:10,color:'rgba(200,192,168,.3)'}}>No high-impact events found for this date</div>
                  )}
                </div>
              </div>
            )}

            <SectionDivider label="Your Notes" icon="📝"/>
            <div style={{marginBottom:8,fontSize:10,color:'rgba(245,240,232,.28)',lineHeight:1.6}}>
              Write your thoughts before and after the trade — or tap 🎙️ to dictate with your voice.
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
              <div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
                  <label className="inp-label" style={{marginBottom:0}}>Pre-Trade Notes</label>
                  <VoiceButton
                    targetField="Pre-Trade Notes"
                    onResult={({ note, emotion }) => {
                      setForm(f => ({ ...f, notes: f.notes ? f.notes + '\n' + note : note }));
                      if (emotion) setForm(f => ({ ...f, emotion }));
                    }}
                  />
                </div>
                <textarea className="inp" rows={4} placeholder="Setup confluences, bias, reasoning… or use voice 🎙️"
                  value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
                {form.notes && form.emotion && (
                  <div style={{marginTop:4,fontSize:9,color:'rgba(201,168,76,.6)',letterSpacing:'.06em'}}>
                    🏷️ Emotion auto-tagged: <strong style={{color:'var(--gold)'}}>{form.emotion}</strong>
                  </div>
                )}
              </div>
              <div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
                  <label className="inp-label" style={{marginBottom:0}}>Post-Trade Review</label>
                  <VoiceButton
                    targetField="Post-Trade Review"
                    onResult={({ note, emotion }) => {
                      setForm(f => ({ ...f, review: f.review ? f.review + '\n' + note : note }));
                      if (emotion) setForm(f => ({ ...f, emotion }));
                    }}
                  />
                </div>
                <textarea className="inp" rows={4} placeholder="What went well? What to improve? or use voice 🎙️"
                  value={form.review} onChange={e=>setForm(f=>({...f,review:e.target.value}))}/>
                {form.review && form.emotion && (
                  <div style={{marginTop:4,fontSize:9,color:'rgba(201,168,76,.6)',letterSpacing:'.06em'}}>
                    🏷️ Emotion auto-tagged: <strong style={{color:'var(--gold)'}}>{form.emotion}</strong>
                  </div>
                )}
              </div>
            </div>
          </>}

          {/* ══ TAB 2: SETUP ══ */}
          {tab===2 && <>
            <SectionDivider label="What Was the Market Doing?" icon="🌍"/>
            <div style={{marginBottom:10,fontSize:10,color:'rgba(245,240,232,.28)',lineHeight:1.6}}>
              Describe the market conditions when you took this trade. Helps you identify which environments suit your strategy.
            </div>
            <div style={{marginBottom:16}}>
              <label className="inp-label">Market Condition</label>
              <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:6}}>
                {MARKET_CONDITIONS.map(mc=>(
                  <Chip key={mc} label={mc} active={form.marketCondition===mc} onClick={()=>set('marketCondition',mc)}/>
                ))}
              </div>
            </div>
            <Row cols={3} mb={4}>
              <Field label="Higher Timeframe Bias">
                <div style={{display:'flex',gap:6,marginTop:4}}>
                  {HTF_BIAS.map(b=>(
                    <Chip key={b} label={b} active={form.htfBias===b}
                      color={b==='Bullish'?'var(--green)':b==='Bearish'?'var(--red)':'var(--gold)'}
                      onClick={()=>set('htfBias',b)}/>
                  ))}
                </div>
              </Field>
              <Field label="Higher Timeframe (HTF)">
                <select className="inp" value={form.htfTf||'H4'} onChange={e=>set('htfTf',e.target.value)}>
                  {TIMEFRAMES.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Entry Timeframe">
                <select className="inp" value={form.entryTf||'M15'} onChange={e=>set('entryTf',e.target.value)}>
                  {TIMEFRAMES.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </Field>

            </Row>
            <div style={{marginBottom:16,fontSize:9,color:'rgba(245,240,232,.22)',paddingLeft:2,lineHeight:1.6}}>
              HTF = the larger chart you used to identify the trend or structure (e.g. H4). Entry TF = the smaller chart where you triggered the trade (e.g. M15).
            </div>

            <SectionDivider label="What Confirmed Your Entry?" icon="🎯"/>
            <div style={{marginBottom:10,fontSize:10,color:'rgba(245,240,232,.28)',lineHeight:1.6}}>
              Confluences are reasons that supported your trade idea. Tag all that applied — the more confluences, the stronger your setup.
            </div>
            <div style={{marginBottom:16}}>
              <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:4,padding:'12px 14px',background:'rgba(255,255,255,.02)',border:'1px solid rgba(255,255,255,.06)',borderRadius:4}}>
                {CONFLUENCE_TAGS.map(tag=>(
                  <Chip key={tag} label={tag} active={(form.confluences||[]).includes(tag)}
                    showIcon={true}
                    onClick={()=>toggleConfluence(tag)}/>
                ))}
                {/* Custom confluences the trader has already added — shown as removable chips */}
                {(form.confluences||[]).filter(t=>!CONFLUENCE_TAGS.includes(t)).map(tag=>(
                  <Chip key={tag} label={tag} active={true} onClick={()=>toggleConfluence(tag)}/>
                ))}
                {/* "Other" — reveals a free-text input so a trader can tag a confluence
                    that isn't in the preset list above */}
                <Chip label={showCustomConfluence ? '✕ Cancel' : '+ Other'} active={showCustomConfluence}
                  onClick={()=>{ setShowCustomConfluence(v=>!v); setCustomConfluenceInput(''); }}/>
              </div>

              {showCustomConfluence && (
                <div style={{display:'flex',gap:8,marginTop:8}}>
                  <input className="inp" placeholder="Type your own confluence…"
                    value={customConfluenceInput}
                    onChange={e=>setCustomConfluenceInput(e.target.value)}
                    onKeyDown={e=>{
                      if (e.key==='Enter') {
                        e.preventDefault();
                        const val = customConfluenceInput.trim();
                        if (val) {
                          const cur = form.confluences || [];
                          if (!cur.includes(val)) set('confluences',[...cur,val]);
                        }
                        setCustomConfluenceInput('');
                        setShowCustomConfluence(false);
                      }
                    }}
                    autoFocus/>
                  <button type="button" onClick={()=>{
                    const val = customConfluenceInput.trim();
                    if (val) {
                      const cur = form.confluences || [];
                      if (!cur.includes(val)) set('confluences',[...cur,val]);
                    }
                    setCustomConfluenceInput('');
                    setShowCustomConfluence(false);
                  }} style={{
                    padding:'0 16px',borderRadius:4,cursor:'pointer',
                    background:'rgba(201,168,76,.15)',border:'1px solid rgba(201,168,76,.35)',
                    color:'var(--gold)',fontSize:10,fontWeight:700,letterSpacing:'.06em',
                    textTransform:'uppercase',fontFamily:"'Inter',sans-serif",flexShrink:0,
                  }}>Add</button>
                </div>
              )}

              <div style={{marginTop:8,display:'flex',alignItems:'center',gap:8}}>
                {(form.confluences||[]).length > 0 ? (
                  <>
                    <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',width:18,height:18,borderRadius:'50%',background:'rgba(201,168,76,.2)',border:'1px solid rgba(201,168,76,.4)',fontSize:9,fontWeight:700,color:'var(--gold)'}}>
                      {(form.confluences||[]).length}
                    </span>
                    <span style={{fontSize:10,color:'rgba(201,168,76,.7)',letterSpacing:'.06em',fontWeight:600}}>
                      confluence{(form.confluences||[]).length>1?'s':''} tagged
                    </span>
                    <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                      {(form.confluences||[]).map(tag=>(
                        <span key={tag} title="Click to remove" onClick={()=>toggleConfluence(tag)}
                          style={{cursor:'pointer',fontSize:9,padding:'1px 7px',background:'rgba(201,168,76,.1)',border:'1px solid rgba(201,168,76,.25)',color:'rgba(201,168,76,.85)',borderRadius:2,letterSpacing:'.04em'}}>{tag} ×</span>
                      ))}
                    </div>
                  </>
                ) : (
                  <span style={{fontSize:10,color:'rgba(245,240,232,.2)',letterSpacing:'.04em'}}>
                    Select all confluences that apply to this setup
                  </span>
                )}
              </div>
            </div>

            <SectionDivider label="Was There News?" icon="📰"/>
            <Row cols={2} mb={6}>
              <Field label="High-Impact News Around This Trade?">
                <div style={{display:'flex',gap:8,marginTop:6}}>
                  <Chip label="✅ Yes" active={form.newsPresent===true}  color="var(--red)" onClick={()=>set('newsPresent',true)}/>
                  <Chip label="❌ No"  active={form.newsPresent===false} color="var(--green)" onClick={()=>set('newsPresent',false)}/>
                </div>
              </Field>
            </Row>
            <div style={{marginBottom:16,fontSize:9,color:'rgba(245,240,232,.22)',paddingLeft:2,lineHeight:1.6}}>
              News events (e.g. CPI, NFP, Fed decisions) can cause extreme volatility. Logging this helps you see whether news trades perform differently.
            </div>

            <SectionDivider label="Chart Screenshots" icon="📸"/>
            <div style={{marginBottom:10,fontSize:10,color:'rgba(245,240,232,.28)',lineHeight:1.6}}>
              Screenshots are optional but powerful for reviewing your trades visually later.
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
              {[['before','Before Trade',prevBefore,setPrevBefore],['after','After Trade',prevAfter,setPrevAfter]].map(([which,lbl,prev,setPrev])=>(
                <div key={which}>
                  <label className="inp-label">{lbl}</label>
                  <input type="file" accept="image/*" id={`img-${which}`} onChange={e=>handleImg(which,e)} style={{display:'none'}}/>
                  <button className="btn-outline" onClick={()=>document.getElementById(`img-${which}`).click()}
                    disabled={imgUploading[which]}
                    style={{marginBottom:6,width:'100%',justifyContent:'center',opacity:imgUploading[which]?.5:1}}>
                    {imgUploading[which] ? (
                      <>
                        <span style={{width:9,height:9,border:'1.5px solid rgba(255,255,255,.2)',borderTopColor:'var(--gold)',borderRadius:'50%',display:'inline-block',animation:'spin 1s linear infinite'}}/>
                        Compressing…
                      </>
                    ) : (
                      <>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                        {prev?'Change':'Upload'}
                      </>
                    )}
                  </button>
                  {prev&&(
                    <div style={{position:'relative',display:'inline-block',width:'100%'}}>
                      <img src={prev} alt={lbl} style={{width:'100%',height:80,objectFit:'cover',border:'1px solid var(--m-border)',borderRadius:3}}/>
                      <button onClick={()=>{setPrev('');set(which==='before'?'imgBefore':'imgAfter','');}}
                        style={{position:'absolute',top:3,right:3,background:'rgba(217,79,79,.85)',border:'none',color:'white',cursor:'pointer',width:18,height:18,fontSize:11,display:'flex',alignItems:'center',justifyContent:'center',borderRadius:2}}>×</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>}

          {/* ══ TAB 1: RESULT ══ */}
          {tab===1 && <>
            <SectionDivider label="Did You Follow Your Rules?" icon="⭐"/>
            <div style={{marginBottom:10,fontSize:10,color:'rgba(245,240,232,.28)',lineHeight:1.6}}>
              Check every rule you followed on this trade. Your Setup Grade and Plan Score are calculated automatically from your selections.
            </div>
            {(()=>{
              const savedRules = (propRules && propRules.length > 0) ? propRules : DEFAULT_RULES;
              const ruleCount  = savedRules.length;
              const checks     = (() => {
                const c = form.ruleChecks || [];
                return Array.from({ length: ruleCount }, (_, i) => c[i] || false);
              })();
              const score = checks.filter(Boolean).length;
              const scoreColor = score === ruleCount ? 'var(--green)' : score >= ruleCount * 0.66 ? 'var(--gold)' : score >= ruleCount * 0.33 ? '#E2A542' : 'var(--red)';
              const noneChecked = score === 0;
              return (
                <div style={{marginBottom:14,padding:'14px 16px',background:noneChecked?'rgba(217,79,79,.04)':'rgba(201,168,76,.04)',border:`1px solid ${noneChecked?'rgba(217,79,79,.25)':'rgba(201,168,76,.12)'}`,borderRadius:4,transition:'border-color .2s, background .2s'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
                    <div style={{display:'flex',alignItems:'center',gap:7}}>
                      <div style={{fontSize:9,letterSpacing:'.14em',textTransform:'uppercase',color:noneChecked?'#D94F4F':'rgba(201,168,76,.7)',fontWeight:700}}>Rule Adherence Score</div>
                      {noneChecked && <span style={{fontSize:7,background:'rgba(217,79,79,.15)',color:'#D94F4F',padding:'1px 6px',letterSpacing:'.1em',fontFamily:"'Inter',sans-serif",fontWeight:700,borderRadius:2}}>REQUIRED</span>}
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:4}}>
                      {Array.from({length:ruleCount},(_,i)=>(
                        <div key={i} style={{width:ruleCount>8?14:24,height:4,borderRadius:2,background:i<score?'var(--gold)':'rgba(255,255,255,.1)',transition:'background .2s'}}/>
                      ))}
                      <span style={{fontSize:11,fontWeight:700,color:scoreColor,marginLeft:4}}>{score}/{ruleCount}</span>
                    </div>
                  </div>
                  {savedRules.map((rule,i)=>(
                    <label key={i} style={{display:'flex',alignItems:'center',gap:10,marginBottom:i<ruleCount-1?8:0,cursor:'pointer'}}>
                      <div onClick={()=>{const n=[...checks];n[i]=!n[i];set('ruleChecks',n);}}
                        style={{width:16,height:16,border:`1.5px solid ${checks[i]?'var(--green)':'rgba(255,255,255,.2)'}`,background:checks[i]?'rgba(61,190,115,.2)':'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,cursor:'pointer',transition:'all .15s',borderRadius:2}}>
                        {checks[i]&&<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                      </div>
                      <span style={{fontSize:12,color:checks[i]?'rgba(240,234,214,.8)':'rgba(200,192,168,.4)',transition:'color .15s'}}>{rule}</span>
                    </label>
                  ))}
                  {noneChecked&&(
                    <div style={{marginTop:10,fontSize:10,color:'rgba(217,79,79,.6)',letterSpacing:'.04em',display:'flex',alignItems:'center',gap:6}}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#D94F4F" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                      Check at least one rule — Setup Grade and Followed Plan are derived from your selection.
                    </div>
                  )}
                </div>
              );
            })()}

            {(()=>{
              const { followedPlan: autoFollowed, setupGrade: autoGrade } = deriveQuality(form.ruleChecks, propRules);
              const hasChecks  = (form.ruleChecks||[]).some(Boolean);
              return (
                <Row cols={3} mb={16}>
                  <Field label={<>Setup Grade<AutoBadge/></>}>
                    <div style={{display:'flex',gap:6,marginTop:4}}>
                      {SETUP_GRADES.map(g => {
                        const isActive = hasChecks ? autoGrade===g : false;
                        const col = g==='A+'?'var(--green)':g==='A'?'#4E7FD4':g==='B'?'var(--gold)':'var(--red)';
                        const rgb = g==='A+'?'61,190,115':g==='A'?'78,127,212':g==='B'?'201,168,76':'217,79,79';
                        return (
                          <div key={g} style={{padding:'5px 12px',border:`1.5px solid ${isActive?`rgba(${rgb},.7)`:'rgba(255,255,255,.08)'}`,borderRadius:3,fontSize:11,fontWeight:700,color:isActive?col:'rgba(245,240,232,.2)',background:isActive?`rgba(${rgb},.15)`:'transparent',boxShadow:isActive?`0 0 12px rgba(${rgb},.2)`:'none',transition:'all .2s'}}>{g}</div>
                        );
                      })}
                    </div>
                    {!hasChecks && <div style={{fontSize:9,color:'rgba(245,240,232,.2)',marginTop:5,letterSpacing:'.05em'}}>Check rules below to auto-grade</div>}
                  </Field>
                  <Field label={<>Followed Plan?<AutoBadge/></>}>
                    <div style={{display:'flex',gap:6,marginTop:4}}>
                      {[{label:'✅ Yes',val:true,color:'var(--green)',rgb:'61,190,115'},{label:'❌ No',val:false,color:'var(--red)',rgb:'217,79,79'}].map(opt => {
                        const isActive = hasChecks && autoFollowed === opt.val;
                        return (
                          <div key={opt.label} style={{padding:'5px 14px',border:`1.5px solid ${isActive?`rgba(${opt.rgb},.7)`:'rgba(255,255,255,.08)'}`,borderRadius:3,fontSize:10,fontWeight:700,color:isActive?opt.color:'rgba(245,240,232,.2)',background:isActive?`rgba(${opt.rgb},.15)`:'transparent',boxShadow:isActive?`0 0 12px rgba(${opt.rgb},.2)`:'none',transition:'all .2s'}}>{opt.label}</div>
                        );
                      })}
                    </div>
                    {!hasChecks && <div style={{fontSize:9,color:'rgba(245,240,232,.2)',marginTop:5,letterSpacing:'.05em'}}>Check rules below to auto-set</div>}
                  </Field>
                </Row>
              );
            })()}

            <Row cols={1} mb={14}>
              <Field label="How Did You Enter?">
                <select className="inp" value={form.entryType||''} onChange={e=>set('entryType',e.target.value)}>
                  <option value="">-- Select entry method --</option>
                  <option value="Limit">Limit — pending order placed in advance</option>
                  <option value="Market">Market — entered at current price immediately</option>
                  <option value="Confirmation">Confirmation — waited for a candle close signal</option>
                  <option value="Stop">Stop — breakout order above/below a level</option>
                </select>
              </Field>
            </Row>

            <SectionDivider label="What Actually Happened  ·  After the Trade" icon="📊"/>
            {/* Outcome hint banner */}
            <div style={{marginBottom:10,fontSize:10,color:'rgba(245,240,232,.35)',lineHeight:1.6,padding:'6px 12px',background:'rgba(78,127,212,.04)',border:'1px solid rgba(78,127,212,.08)',borderRadius:3}}>
              ✅ <strong style={{color:'rgba(78,127,212,.7)'}}>Post-Trade Record</strong> — Fill in the <em>actual</em> prices after your trade closes. The system will auto-detect Win / Loss / Break-Even and calculate your real P&amp;L.
            </div>
            {/* Auto-derived outcome badge */}
            {derivedOutcome && (
              <div style={{marginBottom:12,display:'flex',alignItems:'center',gap:8,padding:'8px 12px',
                background: derivedOutcome.pnl==='win'?'rgba(61,190,115,.08)':derivedOutcome.pnl==='loss'?'rgba(217,79,79,.08)':'rgba(201,168,76,.08)',
                border: `1px solid ${derivedOutcome.pnl==='win'?'rgba(61,190,115,.3)':derivedOutcome.pnl==='loss'?'rgba(217,79,79,.3)':'rgba(201,168,76,.3)'}`,
                borderRadius:4}}>
                <span style={{fontSize:16}}>{derivedOutcome.pnl==='win'?'✅':derivedOutcome.pnl==='loss'?'❌':'⚖️'}</span>
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:derivedOutcome.pnl==='win'?'var(--green)':derivedOutcome.pnl==='loss'?'var(--red)':'var(--gold)'}}>
                    {derivedOutcome.pnl==='win'?'WIN':derivedOutcome.pnl==='loss'?'LOSS':'BREAK-EVEN'}
                    {' · '}{derivedOutcome.exitType}
                  </div>
                  <div style={{fontSize:9,color:'rgba(245,240,232,.35)',marginTop:1}}>
                    Auto-detected from your Actual Entry &amp; Exit · you can override Exit Type below
                  </div>
                </div>
                {actualAmount !== null && (
                  <div style={{marginLeft:'auto',fontSize:15,fontWeight:700,fontFamily:"'Bebas Neue',sans-serif",
                    color:actualAmount>0?'var(--green)':actualAmount<0?'var(--red)':'var(--gold)'}}>
                    {actualAmount>=0?'+':''}{fmt$(actualAmount)}
                  </div>
                )}
              </div>
            )}
            <Row cols={4} mb={16}>
              <Field label="Actual Entry">
                <input className="inp" type="number" step="any" placeholder="Actual fill price" value={form.actualEntry||''} onChange={e=>set('actualEntry',e.target.value)}/>
              </Field>
              <Field label="Actual Exit">
                <input className="inp" type="number" step="any" placeholder="Price you exited at" value={form.actualExit||''} onChange={e=>set('actualExit',e.target.value)}/>
              </Field>
              <Field label={<>Actual R:R<AutoBadge/></>}>
                <input className="inp" readOnly value={actualRR?`${actualRR}R`:'—'}
                  style={{color:actualRR>=(parseFloat(calc.rr)||0)?'var(--green)':'var(--red)'}}/>
              </Field>
              <Field label={<>Exit Type{derivedOutcome && !form.exitType ? <AutoBadge/> : null}</>}>
                <select className="inp"
                  value={form.exitType || derivedOutcome?.exitType || ''}
                  onChange={e=>set('exitType',e.target.value)}
                  style={!form.exitType && derivedOutcome?.exitType ? {color:'rgba(201,168,76,.8)',fontStyle:'italic'} : {}}>
                  <option value="">-- Select --</option>
                  {EXIT_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
                {!form.exitType && derivedOutcome?.exitType && (
                  <div style={{fontSize:8,color:'rgba(201,168,76,.5)',marginTop:2}}>Auto-detected · tap to override</div>
                )}
              </Field>
            </Row>

            <Row cols={2} mb={6}>
              <Field label="MFE — Best Price Reached">
                {(() => {
                  const resolvedExit = form.exitType || derivedOutcome?.exitType || '';
                  const isTPHit = resolvedExit === 'TP Hit';
                  const tpVal = form.tp || '';
                  return (
                    <>
                      <input
                        className="inp"
                        type="number"
                        step="any"
                        placeholder={isTPHit ? 'Price reached TP — MFE = TP' : 'Highest price in your favour'}
                        value={isTPHit ? tpVal : (form.mfe || '')}
                        readOnly={isTPHit}
                        onChange={isTPHit ? undefined : e => set('mfe', e.target.value)}
                        style={isTPHit ? { color: 'var(--green)', opacity: 0.6, cursor: 'not-allowed' } : {}}
                      />
                      <div style={{ fontSize: 8, color: isTPHit ? 'rgba(61,190,115,.4)' : 'rgba(245,240,232,.25)', marginTop: 2 }}>
                        {isTPHit
                          ? '🎯 TP Hit — price reached your target. MFE equals your Take Profit.'
                          : 'How far did price move for you before reversing?'}
                      </div>
                    </>
                  );
                })()}
              </Field>
              <Field label="MAE — Worst Price Reached">
                {(() => {
                  const resolvedExit = form.exitType || derivedOutcome?.exitType || '';
                  const isSLHit = resolvedExit === 'SL Hit';
                  const slPrice = form.actualExit || form.sl || '';
                  return (
                    <>
                      <input
                        className="inp"
                        type="number"
                        step="any"
                        placeholder={isSLHit ? 'SL Hit — MAE = Actual Exit' : 'Deepest price against you'}
                        value={isSLHit ? slPrice : (form.mae || '')}
                        readOnly={isSLHit}
                        onChange={isSLHit ? undefined : e => set('mae', e.target.value)}
                        style={isSLHit ? { color: 'var(--red)', opacity: 0.6, cursor: 'not-allowed' } : {}}
                      />
                      <div style={{ fontSize: 8, color: isSLHit ? 'rgba(217,79,79,.4)' : 'rgba(245,240,232,.25)', marginTop: 2 }}>
                        {isSLHit
                          ? '🛑 SL Hit — price reached your stop. MAE equals your Actual Exit.'
                          : 'How far did price move against you?'}
                      </div>
                    </>
                  );
                })()}
              </Field>
            </Row>

            <SectionDivider label="How Did You Manage It?" icon="🎛️"/>
            <div style={{marginBottom:8,fontSize:10,color:'rgba(245,240,232,.28)',lineHeight:1.6}}>
              Tick any actions you took while the trade was open.
            </div>
            <div style={{display:'flex',gap:isMobile?7:10,flexWrap:'wrap',marginBottom:16}}>
              {[['movedSl','↔️ Moved Stop Loss'],['partialClose','✂️ Took Partial Profits'],['heldToTp','🎯 Held Full Position to Target']].map(([k,lbl])=>(
                <Chip key={k} label={lbl} active={form[k]===true} onClick={()=>set(k,!form[k])}/>
              ))}
            </div>
          </>}

          {/* ══ TAB 3: MINDSET ══ */}
          {tab===3 && <>
            <SectionDivider label="How Were You Feeling?" icon="❤️"/>
            <div style={{marginBottom:10,fontSize:10,color:'rgba(245,240,232,.28)',lineHeight:1.6}}>
              Your emotional state directly affects decision-making. Honest logging helps you spot patterns — like only winning when calm, or losing after a revenge trade.
            </div>
            <div style={{marginBottom:16}}>
              <label className="inp-label">Primary Emotion During This Trade</label>
              <div style={{display:'flex',gap:7,flexWrap:'wrap',marginTop:6}}>
                {EMOTIONS.map(em=>(
                  <Chip key={em} label={em} active={form.emotion===em} onClick={()=>set('emotion',em)}/>
                ))}
              </div>
            </div>

            <SectionDivider label="Rate Your Performance" icon="📏"/>
            <div style={{marginBottom:10,fontSize:10,color:'rgba(245,240,232,.28)',lineHeight:1.6}}>
              Rate from 1 (very low) to 5 (excellent). Be honest — this data trains your personal AI coach over time.
            </div>
            <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'repeat(3,1fr)',gap:isMobile?10:16,marginBottom:16}}>
              {[
                ['confidence','Pre-Trade Confidence','var(--green)','How sure were you about this setup?'],
                ['discipline','Discipline Score','var(--blue)','Did you stick to your rules without impulse?'],
                ['stressLevel','Stress Level','var(--red)','How stressed or anxious did you feel?'],
              ].map(([k,lbl,col,hint])=>(
                <div key={k}>
                  <label className="inp-label">{lbl}</label>
                  <StarRating value={form[k]||0} onChange={v=>set(k,v)} color={col}/>
                  <div style={{fontSize:8,color:'rgba(245,240,232,.22)',marginTop:3,lineHeight:1.4}}>
                    {form[k] ? `${form[k]}/5` : hint}
                  </div>
                </div>
              ))}
            </div>

            {form.emotion && (form.confidence>0||form.discipline>0) && (
              <div style={{padding:'10px 14px',background:'rgba(201,168,76,.06)',border:'1px solid rgba(201,168,76,.12)',marginBottom:16,fontSize:10,color:'rgba(240,234,214,.5)',lineHeight:1.7,borderRadius:4}}>
                <span style={{color:'var(--gold)',fontFamily:"'Inter',sans-serif",fontSize:8,letterSpacing:'.12em',fontWeight:700}}>AI WILL ANALYZE: </span>
                Emotion: <strong style={{color:'var(--gold)'}}>{form.emotion}</strong>
                {form.confidence>0 && <> · Confidence: <strong style={{color:'var(--green)'}}>{form.confidence}/5</strong></>}
                {form.discipline>0 && <> · Discipline: <strong style={{color:'var(--blue)'}}>{form.discipline}/5</strong></>}
              </div>
            )}
          </>}

        </div>{/* end scrollable body */}

        {/* ── Validation error banner ── */}
        {validationErrors.length > 0 && (
          <div style={{
            margin: '0 0 0 0', padding: '10px 16px',
            background: 'rgba(217,79,79,.08)', borderTop: '1px solid rgba(217,79,79,.2)',
            display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, fontWeight: 700, color: 'var(--red)', fontFamily: "'Inter',sans-serif" }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {validationErrors.length === 1 ? 'Fix this before saving' : `${validationErrors.length} issues to fix`}
              <button onClick={() => setValidationErrors([])}
                style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'rgba(217,79,79,.5)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
            </div>
            {validationErrors.length > 1 && (
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 11, color: 'rgba(240,200,200,.7)', lineHeight: 1.8, fontFamily: "'Inter',sans-serif" }}>
                {validationErrors.map((e,i) => <li key={i}>{e}</li>)}
              </ul>
            )}
            {validationErrors.length === 1 && (
              <div style={{ fontSize: 11, color: 'rgba(240,200,200,.75)', paddingLeft: 19, fontFamily: "'Inter',sans-serif" }}>{validationErrors[0]}</div>
            )}
          </div>
        )}

        {/* ── API save error ── */}
        {saveError && (
          <div style={{
            padding: '10px 16px', flexShrink: 0,
            background: 'rgba(217,79,79,.08)', borderTop: '1px solid rgba(217,79,79,.2)',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2.5" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <span style={{ fontSize: 11, color: 'rgba(240,200,200,.85)', flex: 1, fontFamily: "'Inter',sans-serif" }}>{saveError}</span>
            <button onClick={() => setSaveError('')} style={{ background: 'none', border: 'none', color: 'rgba(217,79,79,.5)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
          </div>
        )}

        {/* ── Footer ── */}
        <div style={{
          padding: isMobile ? '12px 12px' : '14px 22px',
          borderTop: '1px solid rgba(255,255,255,.06)',
          display: 'flex', alignItems: 'center', gap: 8,
          flexShrink: 0, background: 'rgba(255,255,255,.01)',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
        }}>
          {/* Save button — full width on mobile */}
          <button onClick={handleSave} disabled={saving} style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            padding: '11px 18px', borderRadius: 6, cursor: saving ? 'not-allowed' : 'pointer',
            background: 'linear-gradient(135deg,rgba(201,168,76,.22),rgba(201,168,76,.12))',
            border: '1px solid rgba(201,168,76,.4)', color: 'var(--gold)',
            fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
            fontFamily: "'Inter',sans-serif", opacity: saving ? .6 : 1,
            flex: isMobile ? '1 1 100%' : '0 0 auto',
            order: isMobile ? 0 : 0,
          }}>
            {saving ? (
              <>
                <span style={{width:11,height:11,border:'1.5px solid rgba(201,168,76,.3)',borderTopColor:'var(--gold)',borderRadius:'50%',display:'inline-block',animation:'spin 1s linear infinite'}}/>
                Saving…
              </>
            ) : (
              <>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                {initial?.id ? 'Update Trade' : 'Save Trade'}
              </>
            )}
          </button>
          {tab < TABS.length - 1 && (
            <button onClick={() => setTab(t => t+1)} disabled={saving} style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              padding: '11px 14px', borderRadius: 6, cursor: 'pointer',
              background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)',
              color: 'rgba(240,234,214,.6)', fontSize: 11, fontWeight: 700,
              letterSpacing: '.06em', textTransform: 'uppercase', fontFamily: "'Inter',sans-serif",
              flex: isMobile ? 1 : '0 0 auto',
            }}>
              {isMobile ? `Next` : `Next: ${TABS[tab+1].label}`}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          )}
          <button onClick={onClose} disabled={saving} style={{
            padding: '11px 14px', borderRadius: 6, cursor: 'pointer',
            background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)',
            color: 'rgba(240,234,214,.45)', fontSize: 11, fontWeight: 700,
            letterSpacing: '.06em', textTransform: 'uppercase', fontFamily: "'Inter',sans-serif",
            flex: isMobile ? 1 : '0 0 auto',
            marginLeft: (!isMobile && tab < TABS.length - 1) ? 'auto' : undefined,
          }}>Cancel</button>
          {!isMobile && (
            <div className="kfx-tab-dots">
              {TABS.map((_,i) => (
                <button key={i} className={`kfx-tab-dot${tab===i?' active':''}`}
                  onClick={() => setTab(i)} title={TABS[i].label}/>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>

    {confirmDialog && (
      <ConfirmDialog
        title="Missing Price Data"
        message="Entry, SL, TP or Lot Size is missing — P&L will be saved as $0. You can edit the trade later to add the correct values."
        onConfirm={confirmDialog.onConfirm}
        onCancel={()=>setConfirmDialog(null)}
      />
    )}
  </>
  );
}